import {useContext, useEffect, useState} from 'react';

import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import {Box, Grid, IconButton, Typography} from '@mui/material';

import {MatchInformation} from '../DetailedCardView/MatchInformation/MatchInformation';

import {ActionButton} from './ActionButton';
import {ApplicantKeyFacts} from './ApplicantKeyfacts';
import {ApplicantSkills} from './ApplicantSkills';
import {CollapsedMatchCultureInfo} from './CollapsedMatchCultureInfo';
import {CompanyMatchNotes} from './CompanyMatchNotes';
import {ContactInformation} from './ContactInformation';
import {Languages} from './Languages';
import {MatchmakerNotes} from './MatchMakerNotes';
import {WorkExperiences} from './WorkExperiences';
import {LockedCultureTypeBanner} from '../../../../../../components/Banner/LockedCultureTypeBanner/LockedCultureTypeBanner';
import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {ErrorMessage} from '../../../../../../components/ErrorMessage/ErrorMessage';
import {Loading} from '../../../../../../components/Loading/Loading';
import MatchTags from '../../../../../../components/MatchTags/MatchTags';
import {RejectionReasonModal} from '../../../../../../components/RejectionReasonModal/RejectionReasonModal';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../../../contexts/UserContext/UserContext';
import {useApiRead} from '../../../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {orangeColors} from '../../../../../../layouts/theme/designSystem';
import {GetRoutes} from '../../../../../../lib/api/GET/GetRoutes';
import {Patch} from '../../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../../lib/api/PATCH/PatchRoutes';
import {MATCH_DETAILS_QUERY_KEY} from '../../../../../../pages/company/recruiting/MatchDetailsPage';
import {MatchStatus} from '../../../../../../types/MatchStatus/MatchStatus';
import {MatchData, SupportedTagBE} from '../../../../../../types/shared-recruiting-types';
import {CompanyAdminRoleData} from '../../../../../../types/User';
import {CultureOverview} from '../../../../../applicant/dashboard/components/culture-overview/CultureOverview';

interface Props {
  matchId: string;
  onSuccess: () => void;
  refreshMatchTags: (value: MatchData) => void;
}

export const DetailedCardView = ({matchId, onSuccess, refreshMatchTags}: Props) => {
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t: tr} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.menuItems'});
  const queryClient = useQueryClient();

  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    data,
    error: matchError,
    isPending: isMatchPending,
  } = useApiRead<{companyMatch: MatchData}>({
    path: GetRoutes.companyMatchDetails(companyId, matchId),
    options: {
      enabled: !!matchId && !!companyId,
      queryKey: [MATCH_DETAILS_QUERY_KEY, companyId, matchId],
    },
  });

  const [isModalOpen, setModalOpen] = useState(false);
  const [collapse, setCollapse] = useState<boolean>(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeStatusMenu = () => {
    setAnchorEl(null);
  };

  const handleDeleteTag = (tag: SupportedTagBE) => {
    if (data?.companyMatch?.tags) {
      data.companyMatch.tags = data?.companyMatch?.tags?.filter((t: SupportedTagBE) => t.resourceId !== tag.resourceId);
      refreshMatchTags(data?.companyMatch);
    }
  };

  const handleAddTag = (tag: SupportedTagBE) => {
    if (data?.companyMatch && !data?.companyMatch?.tags) {
      data.companyMatch.tags = [];
    }

    if (data?.companyMatch?.tags.filter((t: SupportedTagBE) => t.tagLabel === tag.tagLabel).length === 0) {
      data.companyMatch.tags = [...data.companyMatch.tags, tag];
      refreshMatchTags(data?.companyMatch);
    }
  };

  const successHandler = (status: MatchStatus) => {
    if (data?.companyMatch) {
      const update = {...data.companyMatch, status};
      queryClient.setQueryData([MATCH_DETAILS_QUERY_KEY, companyId, matchId], {companyMatch: update});
    }
    closeStatusMenu();
    dispatchMessage(t('companyApplicants.snackbars.success'), 'success');
    onSuccess();
  };

  const {makeRequest: makeRequestContact, error: isContactError} = useApiWrite<Patch.ContactMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.ContactMatch](companyId, matchId),
    onSuccess: () => {
      successHandler(MatchStatus.Contacted);
    },
  });

  const {makeRequest: makeRequestInterview, error: isInterviewError} = useApiWrite<Patch.InterviewMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.InterviewMatch](companyId, matchId),
    onSuccess: () => {
      successHandler(MatchStatus.Interview);
    },
  });

  const {makeRequest: makeRequestHire, error: isHireError} = useApiWrite<Patch.HireMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.HireMatch](companyId, matchId),
    onSuccess: () => {
      successHandler(MatchStatus.Hired);
    },
  });

  const {makeRequest: makeRequestAccept, error: isAcceptError} = useApiWrite<Patch.ReAcceptMatchByCompany>({
    method: 'patch',
    path: PatchRoutes[Patch.ReAcceptMatchByCompany](companyId, matchId),
    onSuccess: () => {
      successHandler(MatchStatus.Accepted);
    },
  });

  const {makeRequest: makeRequestOnHold, error: isOnHoldError} = useApiWrite<Patch.OnHoldMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.OnHoldMatch](companyId, matchId),
    onSuccess: () => {
      successHandler(MatchStatus.OnHold);
    },
  });

  useEffect(() => {
    if (isContactError || isInterviewError || isHireError || isAcceptError || isOnHoldError) {
      dispatchMessage(t('companyApplicants.snackbars.error'), 'error');
    }
  }, [isContactError, isInterviewError, isHireError, isAcceptError, isOnHoldError, dispatchMessage, t]);

  const handleRejectSuccess = () => {
    dispatchMessage(t('shared.tags.statusUpdate'), 'success');
    setModalOpen(false);
    successHandler(MatchStatus.Rejected);
  };
  const {makeRequest, isLoading, error} = useApiWrite<Patch.RejectMatchByCompany>({
    method: 'patch',
    path: PatchRoutes[Patch.RejectMatchByCompany](companyId, matchId),
    onSuccess: handleRejectSuccess,
  });

  if (matchError) {
    return <ErrorMessage />;
  }

  if (isMatchPending) {
    return (
      <Grid container mb={2} minHeight={'100vh'}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            minHeight: '80vh',
          }}
        >
          <Loading />
        </Box>
      </Grid>
    );
  }

  if (!data?.companyMatch) {
    return null;
  }

  const menuItemsActionButton = [
    {
      key: 'accepedAt',
      label: tr('new'),
      onClick: async () => {
        makeRequestAccept();
      },
      variant: 'body1',
      disabled: data.companyMatch.status === MatchStatus.Accepted,
    },
    {
      key: 'contactedAt',
      label: tr('contacted'),
      onClick: async () => {
        makeRequestContact();
      },
      variant: 'body1',
      disabled: data.companyMatch.status === MatchStatus.Contacted,
    },
    {
      key: 'interviewAt',
      label: tr('interview'),
      onClick: async () => {
        makeRequestInterview();
      },
      variant: 'body1',
      disabled: data.companyMatch.status === MatchStatus.Interview,
    },
    {
      key: 'hiredAt',
      label: tr('hired'),
      onClick: async () => {
        makeRequestHire();
      },
      variant: 'body1',
      disabled: data.companyMatch.status === MatchStatus.Hired,
    },
    {
      key: 'onHoldAt',
      label: tr('onHold'),
      onClick: async () => {
        makeRequestOnHold();
      },
      variant: 'body1',
      disabled: data.companyMatch.status === MatchStatus.OnHold,
    },
    {
      key: 'rejectedAt',
      label: tr('rejected'),
      onClick: openModal,
      variant: 'body1',
      disabled: data.companyMatch.status === MatchStatus.Rejected,
    },
  ];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <Grid id='details-card-view'>
      {/* Tags and actions buttons */}
      <Grid container mb={2}>
        <Grid item xs={12} md={8} display={'flex'} flexWrap={'wrap'}>
          <MatchTags
            match={data.companyMatch}
            companyId={companyId}
            onDeleteTagCallback={handleDeleteTag}
            onAddTagCallback={handleAddTag}
          />
        </Grid>
        <Grid
          container
          item
          xs={12}
          md={4}
          sx={{mt: {xs: 2, md: 0}, justifyContent: {xs: 'flex-start', md: 'space-around'}}}
        >
          <Grid item>
            <ActionButton
              status={data.companyMatch.status}
              menuItems={menuItemsActionButton}
              handleClick={handleClick}
              handleClose={closeStatusMenu}
              anchorEl={anchorEl}
            />
          </Grid>
        </Grid>
        <EmpionDialog
          open={isModalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
        >
          <Grid container justifyContent={'flex-end'}>
            <IconButton
              onClick={() => {
                setModalOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <RejectionReasonModal
            rejectionType='company'
            onReject={makeRequest}
            isUpdatingMatch={isLoading}
            error={error}
          />
        </EmpionDialog>
      </Grid>
      <Grid container mb={4}>
        {/* Contact Information */}
        <Grid container item xs={12} sm={6} direction='column'>
          <ContactInformation companyMatchDetails={data.companyMatch} />
        </Grid>
        {/* Key Facts */}
        <Grid container item xs={12} sm={6} direction='column'>
          {data.companyMatch?.applicant && <ApplicantKeyFacts applicant={data.companyMatch.applicant} />}
        </Grid>
      </Grid>
      {/* Matchmaker notes */}
      <Grid container item mb={2}>
        <MatchmakerNotes matchMakerNotes={data.companyMatch?.matchmakerNotes} />
      </Grid>
      {/* Company Admin notes */}
      <Grid container item mb={4}>
        <CompanyMatchNotes
          companyId={companyId}
          matchId={matchId}
          userId={(currentUser?.role.data as CompanyAdminRoleData).companyAdminId}
        />
      </Grid>

      {/* Match Information */}
      <Grid container item mb={4}>
        <Typography variant='h3' mb={1}>
          {t('matchInformation.title')}
        </Typography>
        <MatchInformation data={data.companyMatch} />
      </Grid>

      {/* Culture Type Overview */}
      <Grid container item mb={4}>
        {data.companyMatch?.applicant?.cultureType ? (
          <>
            <Grid item xs={12} mb={1}>
              <Typography variant='h3'>
                {t('companyApplicants.applicantsCard.cardDetails.matchcultureInformation')}
              </Typography>
            </Grid>

            <CultureOverview
              cultureInformation={{cultureType: data.companyMatch?.applicant?.cultureType}}
              backgroundColor={orangeColors[5]}
              shouldHideGradientColor
              onClick={() => setCollapse(!collapse)}
              detailedCardView
              open={collapse}
            />
            <Grid container display={'flex'} alignItems={'center'} justifyContent={'center'} flexDirection={'column'}>
              <CollapsedMatchCultureInfo
                percent={data.companyMatch.culturalFitScore || ''}
                applicantName={data.companyMatch.applicant.personalInformation?.firstName || ''}
                collapse={collapse}
                items={data.companyMatch.items}
              />
            </Grid>
          </>
        ) : (
          <LockedCultureTypeBanner isCompany applicantId={data.companyMatch.applicant.resourceId} />
        )}
      </Grid>

      {/* Skills section */}
      <Grid container item mb={2}>
        <ApplicantSkills applicantSkills={data.companyMatch.applicant.skills} />
      </Grid>
      {/* Work experiences */}
      <Grid container item mb={2}>
        <WorkExperiences workExperiences={data.companyMatch.applicant.workExperiences} />
      </Grid>
      {/* Languages */}
      <Grid container item mb={2}>
        <Languages languages={data.companyMatch.applicant?.languages} />
      </Grid>
    </Grid>
  );
};
